import CircleIcon from '@mui/icons-material/Circle';
import { TimelineItem } from '@mui/lab';
import { Box, Grid, styled, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getCrmJobById,
  CrmJobDTO,
  getActivityLogs,
  getActivityLogsByEntity,
  ActivityLogDTO,
} from 'wisestep-api';
import {
  getFormatDate,
  WsColors,
  WsTypoColors,
  WsTypoVariants,
  ScrollableButtonTabs,
  WsTypography,
  WsTimeLine,
  formatDateInDayMonthYearTime,
  WsPageHeader,
  WsDrawer,
} from 'wisestep-base';

import { IBreadCrumbsList, WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import { WsTabs } from '../../../components/WsTabs';
import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import { WsActivityProps } from '../../../features/activityLog/ActivityHelper';
import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import WsLink from '../../../features/teams/components/WsLink';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { notesAmplitudeEvents } from '../../clientDetails/components/notes/helper/notesHelper';

import ConvertToLeadDrawer from './ConvertToLeadDrawer';
import CrmContactsDrawer from './CrmContactsDrawer';

const crmJobLayoutTabNames = () => [
  { tabName: 'Job Details', tabValue: 'job-details' },
  { tabName: 'Activity', tabValue: 'activity' },
];

const getCrmJobBreadCrumbs = (jobTitle: string): IBreadCrumbsList[] => [
  { label: 'Jobs CRM', url: '/crm-jobs' },
  { label: jobTitle, url: '' },
];

const StyledBox = styled(Box)(() => ({
  border: 1,
  borderColor: WsColors.BorderGrey,
  borderRadius: 1,
  p: 2,
  minHeight: 200,
  backgroundColor: WsColors.TertiaryShades50,
}));

const CrmJobDetails = () => {
  const params = useParams<{ id: string }>();
  const [crmJobInfo, setCrmJobInfo] = useState<CrmJobDTO>();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [parentTabIndex, setParentTabIndex] = useState<number>(0);
  const [getActivityData, setgetActivityData] = useState<ActivityLogDTO[]>([]);
  const workspace = useWorkspaceContext();
  const moreOptionList = ['Convert to Lead', 'View Eligible Contacts', 'View Mailing Contacts'];
  const [activeDrawer, setActiveDrawer] = useState<
    'eligibleContacts' | 'taggedContacts' | 'convertToLead' | null
  >(null);
  const [convertToLeadId, setConvertToLeadId] = useState<string>();

  const getCrmJob = useCallback(async () => {
    if (params.id === undefined) {
      setNotification(failed(`Id not found in the url`));
      return;
    }
    try {
      const response = await getCrmJobById(params.id as string, true);
      response?.data && setCrmJobInfo(response.data);
    } catch (error) {
      setNotification(failed(`Failed to fetch CRM Job Information`));
    }
  }, [params.id, setNotification]);

  useEffect(() => {
    getCrmJob();
  }, [getCrmJob]);

  const activityLists: WsActivityProps[] = [];

  useEffect(() => {
    let avtivityLog: ActivityLogDTO[] = [];
    const fetchDataLog = async () => {
      if (crmJobInfo?.id) {
        try {
          const { data: actvityResponseContext } = await getActivityLogs(crmJobInfo?.id ?? '');
          const { data: actvityResponseEnitiy } = await getActivityLogsByEntity(
            crmJobInfo?.id ?? ''
          );
          avtivityLog = [...(actvityResponseContext || []), ...(actvityResponseEnitiy || [])];
          setgetActivityData(avtivityLog);
        } catch {
          setNotification(failed(`Failed to fetch job activity Information`));
        }
      }
    };
    fetchDataLog();
  }, [crmJobInfo?.id, setNotification]);

  getActivityData
    ?.filter(
      (data: ActivityLogDTO) => data.field === 'CRM Job' && data.entityId == (params.id as string)
    )
    .map((data: ActivityLogDTO) =>
      activityLists.push({
        activity: (
          <TimelineItem>
            <Grid container>
              <Grid item xs={12}>
                <WsTypography color={WsTypoColors.TERTIARY}>
                  {` At ${formatDateInDayMonthYearTime(data?.createdOn?.toString())} by ${
                    data.createdByName
                  }`}
                </WsTypography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant={WsTypoVariants.SUB_TITLE}>
                  {`${data.field} Category -> ${data.description} job ${data.parentEntitiyName}`}
                </Typography>
              </Grid>
            </Grid>
          </TimelineItem>
        ),
        leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize={'small'} />,
      })
    );

  const breadCrumbsList: IBreadCrumbsList[] = getCrmJobBreadCrumbs(
    crmJobInfo?.title ?? 'Job Details'
  );

  const handleTabIndexChange = (index: number) => {
    const tabs = crmJobLayoutTabNames();
    tabs?.[index].tabName === 'Activity' && notesAmplitudeEvents('note_job', 'Note Job', workspace);
    setParentTabIndex(index);
  };

  const onNavigateToEligibleContacts = () => setActiveDrawer('eligibleContacts');

  const onNavigateToMailingContacts = () => setActiveDrawer('taggedContacts');

  const handleConvertToLead = (crmJobId: string) => {
    setConvertToLeadId(crmJobId);
    setActiveDrawer('convertToLead');
  };

  const onMoreOptionClick = (option: string) => {
    switch (option) {
      case 'Convert to Lead':
        handleConvertToLead(crmJobInfo?.id ?? '');
        break;
      case 'View Eligible Contacts':
        onNavigateToEligibleContacts();
        break;
      case 'View Mailing Contacts':
        onNavigateToMailingContacts();
        break;
      default:
        break;
    }
  };

  const formatLocation = (location: any) =>
    location ? [location.city, location.state, location.country].filter(Boolean).join(' ') : '';

  return (
    <>
      <WsBreadCrumbs breadCrumbsList={breadCrumbsList ?? []} maxItems={4} />
      <WsPageHeader
        title={crmJobInfo?.title ?? 'Job Details'}
        subtitle={`Created on ${getFormatDate(crmJobInfo?.createdOn?.toString() ?? '')}`}
        moreOptions={moreOptionList}
        onMoreOptionClick={(option) => onMoreOptionClick(option)}
        badgeLable={crmJobInfo?.jobStatus ? crmJobInfo.jobStatus.statusName : 'Open'}
        socialUrl={crmJobInfo?.linkedInJobUrl}
      />
      {crmJobInfo && (
        <Box borderRadius={2}>
          <Grid item borderRadius={2} sx={{ background: WsColors.White }} mt={2} mb={1}>
            <ScrollableButtonTabs
              scrollList={crmJobLayoutTabNames()}
              getTabIndex={handleTabIndexChange}
              defaultValue={parentTabIndex}
            />
          </Grid>

          {parentTabIndex == 0 && (
            <Box sx={{ background: WsColors.White, p: 3, borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Location
                  </WsTypography>
                  <WsTypography variant={WsTypoVariants.BODY1}>
                    {formatLocation(crmJobInfo.location)}
                  </WsTypography>
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Client
                  </WsTypography>
                  {crmJobInfo.client?.name ? (
                    <WsLink
                      to={`/client-details?clientId=${crmJobInfo.client.id}&isChild=${
                        crmJobInfo.client.parentId == null
                      }&tabIndex=0`}
                    >
                      <WsTypography
                        variant={WsTypoVariants.BODY1}
                        color={WsTypoColors.PRIMARY}
                        cursor="pointer"
                      >
                        {crmJobInfo.client.name}
                      </WsTypography>
                    </WsLink>
                  ) : (
                    <WsTypography variant={WsTypoVariants.BODY1}>N/A</WsTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Contacts
                  </WsTypography>
                  {crmJobInfo.client?.id ? (
                    <WsLink
                      to={`/client-details?tabIndex=${
                        crmJobInfo.client.parentId == null ? 5 : 4
                      }&isProfile=true&isChild=${crmJobInfo.client.parentId == null}&name=${
                        crmJobInfo.client.name
                      }&clientId=${crmJobInfo.client.id}&breadCrumbList=${getEncodedQueryParams(
                        ClintPageBreadCrumb['CLIENTS']
                      )}`}
                    >
                      <WsTypography
                        variant={WsTypoVariants.BODY1}
                        cursor="pointer"
                        color={WsTypoColors.PRIMARY}
                      >
                        {crmJobInfo.clientContactCount ?? 0}
                      </WsTypography>
                    </WsLink>
                  ) : (
                    <WsTypography variant={WsTypoVariants.BODY1}>
                      {crmJobInfo.clientContactCount ?? 0}
                    </WsTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Eligible Contacts
                  </WsTypography>
                  <Box onClick={onNavigateToEligibleContacts}>
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={WsTypoColors.PRIMARY}
                      cursor="pointer"
                    >
                      {crmJobInfo.eligibleContactCount ?? 0}
                    </WsTypography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Mailing Contacts
                  </WsTypography>
                  <Box onClick={onNavigateToMailingContacts}>
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={WsTypoColors.PRIMARY}
                      cursor="pointer"
                    >
                      {crmJobInfo.taggedContactCount ?? 0}
                    </WsTypography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Source
                  </WsTypography>
                  <WsTypography variant={WsTypoVariants.BODY1}>
                    {crmJobInfo.jobSource ?? ''}
                  </WsTypography>
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Created By
                  </WsTypography>
                  {crmJobInfo.createdByName && crmJobInfo.createdBy ? (
                    <WsLink
                      to={`/users/details-page?email=${crmJobInfo.createdBy}&isLink=true&isUserAnalytics=true`}
                    >
                      <WsTypography
                        variant={WsTypoVariants.BODY1}
                        color={WsTypoColors.PRIMARY}
                        cursor="pointer"
                      >
                        {crmJobInfo.createdByName}
                      </WsTypography>
                    </WsLink>
                  ) : (
                    <WsTypography variant={WsTypoVariants.BODY1}>
                      {crmJobInfo.createdByName ?? 'N/A'}
                    </WsTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={2.4}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                    Owner Name
                  </WsTypography>
                  {crmJobInfo.owner?.name && crmJobInfo.owner?.email ? (
                    <WsLink
                      to={`/users/details-page?email=${crmJobInfo.owner.email}&isLink=true&isUserAnalytics=true`}
                    >
                      <WsTypography
                        variant={WsTypoVariants.BODY1}
                        color={WsTypoColors.PRIMARY}
                        cursor="pointer"
                      >
                        {crmJobInfo.owner.name}
                      </WsTypography>
                    </WsLink>
                  ) : (
                    <WsTypography variant={WsTypoVariants.BODY1}>
                      {crmJobInfo.owner?.name ?? 'N/A'}
                    </WsTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                      Job Description
                    </WsTypography>
                  </Box>
                  <StyledBox>
                    {crmJobInfo.jobDescription ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: crmJobInfo.jobDescription,
                        }}
                      />
                    ) : (
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY}>
                        No job description available
                      </WsTypography>
                    )}
                  </StyledBox>
                </Grid>
              </Grid>
            </Box>
          )}

          {parentTabIndex == 1 && (
            <WsTabs
              onChange={(index) =>
                notesAmplitudeEvents(
                  index == 0 ? 'job_details_notes' : 'job_details_activities',
                  'Job Details',
                  workspace
                )
              }
            >
              <WsTabs.Item label="Activities">
                {getActivityData && (
                  <Grid item xs={12} borderRadius={2} sx={{ background: WsColors.White }}>
                    <Box ml="-97%">
                      <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                    </Box>
                  </Grid>
                )}
              </WsTabs.Item>
            </WsTabs>
          )}
        </Box>
      )}

      {activeDrawer === 'eligibleContacts' && crmJobInfo?.id && (
        <WsDrawer
          open={true}
          onClose={() => {
            setActiveDrawer(null);
            getCrmJob();
          }}
          title="Eligible Contacts Access"
          headerSize="medium"
          size="medium"
        >
          <CrmContactsDrawer jobId={crmJobInfo.id} />
        </WsDrawer>
      )}

      {activeDrawer === 'taggedContacts' && crmJobInfo?.id && (
        <WsDrawer
          open={true}
          onClose={() => {
            setActiveDrawer(null);
            getCrmJob();
          }}
          title="Mailing Contacts"
          headerSize="medium"
          size="medium"
        >
          <CrmContactsDrawer jobId={crmJobInfo.id} defaultTab={1} />
        </WsDrawer>
      )}

      {activeDrawer === 'convertToLead' && convertToLeadId && (
        <ConvertToLeadDrawer
          open={true}
          onClose={() => setActiveDrawer(null)}
          crmJobId={convertToLeadId}
        />
      )}
    </>
  );
};

export default CrmJobDetails;
